import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const CountrySpendingAnalysis = () => {
  const [expandedInsights, setExpandedInsights] = useState({});

  const countryData = [
    {
      country: 'UAE',
      flag: '🇦🇪',
      planned: 30,
      actual: 35,
      variance: 16.67,
      spend: 6300000,
      plannedSpend: 5400000,
      comment: 'Overspending due to increased Digital campaigns during Dubai Shopping Festival',
      status: 'overspend',
      insights: 'Dubai Shopping Festival drove 28% higher engagement rates. Digital channels showing 3.8x ROAS. Consider maintaining elevated spend through Q2.'
    },
    {
      country: 'Saudi Arabia',
      flag: '🇸🇦',
      planned: 40,
      actual: 34,
      variance: -15.00,
      spend: 6120000,
      plannedSpend: 7200000,
      comment: 'Underspending in TV due to Ramadan programming delays',
      status: 'underspend',
      insights: 'Ramadan content delays created opportunity cost of $1.08M. TV inventory now available at 15% discount for Q2. Recommend accelerated booking.'
    },
    {
      country: 'Kuwait',
      flag: '🇰🇼',
      planned: 10,
      actual: 8,
      variance: -20.00,
      spend: 1440000,
      plannedSpend: 1800000,
      comment: 'Underspending in Outdoor - billboard approvals delayed by municipality',
      status: 'underspend',
      insights: 'Municipal approval delays affecting outdoor campaigns. Digital OOH showing 2.3x better performance. Recommend pivot strategy.'
    },
    {
      country: 'Qatar',
      flag: '🇶🇦',
      planned: 8,
      actual: 9,
      variance: 12.50,
      spend: 1620000,
      plannedSpend: 1440000,
      comment: 'Slight overspend due to World Cup legacy events premium',
      status: 'overspend',
      insights: 'World Cup legacy momentum continues. Sports-related content showing 45% higher engagement. ROI remains strong at 4.1x.'
    },
    {
      country: 'Bahrain',
      flag: '🇧🇭',
      planned: 5,
      actual: 6,
      variance: 20.00,
      spend: 1080000,
      plannedSpend: 900000,
      comment: 'Higher digital spend during F1 season',
      status: 'overspend',
      insights: 'F1 season premium justified by 52% lift in brand awareness. Luxury segment showing exceptional performance with 5.2x ROAS.'
    },
    {
      country: 'Egypt',
      flag: '🇪🇬',
      planned: 4,
      actual: 4,
      variance: 0,
      spend: 720000,
      comment: 'On track with planned allocation',
      status: 'on-track'
    },
    {
      country: 'Other Markets',
      planned: 3,
      actual: 4,
      variance: 33.3,
      spend: 720000,
      comment: 'Morocco expansion exceeded initial budget',
      status: 'overspend'
    }
  ];

  const toggleInsights = (country) => {
    setExpandedInsights(prev => ({
      ...prev,
      [country]: !prev[country]
    }));
  };

  const getVarianceBadge = (variance, status) => {
    if (status === 'overspend') {
      return <Badge className="bg-red-100 text-red-800">+{variance.toFixed(1)}%</Badge>;
    } else if (status === 'underspend') {
      return <Badge className="bg-yellow-100 text-yellow-800">{variance.toFixed(1)}%</Badge>;
    } else {
      return <Badge className="bg-green-100 text-green-800">On Track</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'overspend':
        return <TrendingUp className="h-4 w-4 text-red-500" />;
      case 'underspend':
        return <TrendingDown className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-green-500" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const maxPercent = Math.max(...countryData.map(c => Math.max(c.planned, c.actual)));

  return (
    <Card className="shadow-lg border-l-4 border-l-emerald-500">
      <CardHeader>
        <CardTitle className="text-xl font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
          Country-Wise Spending Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {countryData.map((country, index) => (
            <div key={index} className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{country.flag}</span>
                  <span className="font-semibold text-gray-900">{country.country}</span>
                  {getStatusIcon(country.status)}
                </div>
                <div className="flex items-center space-x-3">
                  {getVarianceBadge(country.variance, country.status)}
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">{formatCurrency(country.spend)}</div>
                    <div className="text-xs text-gray-500">vs {formatCurrency(country.plannedSpend || country.spend)} planned</div>
                  </div>
                </div>
              </div>

              {/* Horizontal Bar Chart */}
              <div className="space-y-2">
                {/* Planned Bar */}
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-600 w-12">Plan</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 relative overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-slate-400 to-slate-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${(country.planned / maxPercent) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-8">{country.planned.toFixed(0)}%</span>
                </div>

                {/* Actual Bar */}
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-600 w-12">Actual</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3 relative overflow-hidden">
                    <div 
                      className={`h-3 rounded-full transition-all duration-500 ${
                        country.status === 'overspend' 
                          ? 'bg-gradient-to-r from-red-400 to-red-500' 
                          : country.status === 'underspend'
                          ? 'bg-gradient-to-r from-yellow-400 to-yellow-500'
                          : 'bg-gradient-to-r from-emerald-400 to-emerald-500'
                      }`}
                      style={{ width: `${(country.actual / maxPercent) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-8">{country.actual.toFixed(0)}%</span>
                </div>
              </div>

              {/* Insights Button */}
              <div className="pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleInsights(country.country)}
                  className="text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 p-1 h-auto"
                >
                  <span className="flex items-center space-x-1">
                    <span>Insights</span>
                    {expandedInsights[country.country] ? 
                      <ChevronUp className="h-3 w-3" /> : 
                      <ChevronDown className="h-3 w-3" />
                    }
                  </span>
                </Button>

                {/* Expanded Insights */}
                {expandedInsights[country.country] && (
                  <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="text-xs text-emerald-800">
                      <div className="font-medium mb-1">Analysis</div>
                      <div>{country.insights || country.comment}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default CountrySpendingAnalysis;